import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import ErrorBoundary from "./components/ErrorBoundary";
import CanonicalTag from "./components/CanonicalTag";
import Favicon from "./components/Favicon";
import Navbar from "./components/Navbar";
import Header from "./components/Header";
import Main from "./components/Main";
import Footer from "./components/Footer";
import PrivateRoute from "./components/PrivateRoute";
import Login from "./components/Login";
import StandaloneRedirect from "./components/StandaloneRedirect";
import CallbackPage from "./components/CallbackPage";
import OAuthCallback from "./components/OAuthCallback";
import BlogsPage from "./components/BlogsPage";
import DynamicBlogPost from "./components/DynamicBlogPost";
import SolarCalculator from "./components/SolarCalculator";
import Blog1 from "./components/Blog1";
import ResidentialSolar from "./components/ResidentialSolar";
import CommercialSolar from "./components/CommercialSolar";
import UtilityScaleSolar from "./components/UtilityScaleSolar";
import BlogSolarBenefits from "./components/BlogSolarBenefits";
import BlogCommercial from "./components/BlogCommercial";
import BlogCommercialTax from "./components/BlogCommercialTax";
import BlogSolarModels from "./components/BlogSolarModels";
import BlogMC4 from "./components/BlogMC4";
import BlogMSMESolarEMI from "./components/BlogMSMESolarEMI";
import BlogHiddenCosts from "./components/BlogHiddenCosts";
import ReferralLandingPage from "./components/ReferralLandingPage";
import ReferralTerms from "./components/ReferralTerms";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AdminDashboard from "./components/AdminDashboard";
import OurPresencePage from "./components/OurPresencePage";
import CityLandingPage from "./components/CityLandingPage";
import ColdStorageSolar from "./components/ColdStorageSolar";
import WarehousesSolar from "./components/WarehousesSolar";
import QuoteForm from "./components/QuoteForm";
import UserManagement from "./components/UserManagement";
import AdminQuotes from "./components/AdminQuotes";
import BlogAdmin from "./components/BlogAdmin";
import ContentWriterDashboard from "./components/ContentWriterDashboard";
import SalesQuotes from "./components/SalesQuotes";
import FindLeadsPage from "./components/FindLeadsPage";
import ShortlistedLeads from "./components/ShortlistedLeads";
import UserDetail from "./components/UserDetail";
import WhyChooseUs from "./components/WhyChooseUs";
import AppUpdateChecker from "./components/AppUpdateChecker";
import CacheRefreshButton from "./components/CacheRefreshButton";
import RSSFeed from "./components/RSSFeed";

// Lazy load tracking page components for code splitting
const CustomerDashboard = lazy(() => import("./components/CustomerDashboard"));
const BillingHistory = lazy(() => import("./components/BillingHistory"));
const EmandateSetup = lazy(() => import("./components/EmandateSetup"));
const AdminOpexCustomers = lazy(() => import("./components/AdminOpexCustomers"));

function App() {
  // Fix basename - ensure it's always a valid string
  // Empty string means no basename (root path)
  const basename = process.env.PUBLIC_URL || '';
  
  return (
    <ErrorBoundary>
      <AppUpdateChecker />
      <CacheRefreshButton />
      <Router basename={basename}>
        <Favicon />
        <CanonicalTag />
        <StandaloneRedirect />
        <Switch>
          <Route exact path="/">
            <Navbar />
            <Header />
            <Main />
            <Footer />
          </Route>
          <Route exact path="/login">
            <Login />
          </Route>
          {/* Secret sales routes - noindex */}
          <PrivateRoute exact path="/q">
            <SalesQuotes />
          </PrivateRoute>
          <PrivateRoute exact path="/q/new">
            <QuoteForm />
          </PrivateRoute>
          <PrivateRoute exact path="/q/leads">
            <FindLeadsPage />
          </PrivateRoute>
          <PrivateRoute exact path="/q/leads/shortlisted">
            <ShortlistedLeads />
          </PrivateRoute>
          {/* Admin routes - noindex */}
          <PrivateRoute exact path="/admin/users" role="admin">
            <UserManagement />
          </PrivateRoute>
          <PrivateRoute exact path="/admin/users/:userId" role="admin">
            <UserDetail />
          </PrivateRoute>
          <PrivateRoute exact path="/admin" role="admin">
            <AdminQuotes />
          </PrivateRoute>
          <PrivateRoute exact path="/admin/blogs" role="admin">
            <BlogAdmin />
          </PrivateRoute>
          <PrivateRoute exact path="/writer/blogs" role="contentwriter">
            <ContentWriterDashboard />
          </PrivateRoute>
          <PrivateRoute exact path="/admin/opex-customers" role="admin">
            <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
              <AdminOpexCustomers />
            </Suspense>
          </PrivateRoute>
          {/* Customer tracking routes - lazy loaded for code splitting */}
          <PrivateRoute exact path="/tracking" role="customer">
            <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
              <CustomerDashboard />
            </Suspense>
          </PrivateRoute>
          <PrivateRoute exact path="/tracking/billing" role="customer">
            <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
              <BillingHistory />
            </Suspense>
          </PrivateRoute>
          <PrivateRoute exact path="/tracking/billing/:billId" role="customer">
            <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
              <BillingHistory />
            </Suspense>
          </PrivateRoute>
          <PrivateRoute exact path="/tracking/emandate-setup" role="customer">
            <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
              <EmandateSetup />
            </Suspense>
          </PrivateRoute>
          <PrivateRoute exact path="/tracking/emandate-callback" role="customer">
            <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
              <CustomerDashboard />
            </Suspense>
          </PrivateRoute>
          <Route exact path="/blogs">
            <BlogsPage />
          </Route>
          <Route exact path="/rss.xml">
            <RSSFeed />
          </Route>
          <Route exact path="/feed.xml">
            <RSSFeed />
          </Route>
          {/* Specific blog routes - must come before dynamic route */}
          <Route exact path="/blogs/rooftop-solar-guide">
            <Blog1 />
          </Route>
          <Route exact path="/blogs/solar-panel-benefits">
            <BlogSolarBenefits />
          </Route>
          <Route exact path="/blogs/commercial-solar-benefits">
            <BlogCommercial />
          </Route>
          <Route exact path="/blogs/commercial-solar-tax-benefits">
            <BlogCommercialTax />
          </Route>
          <Route exact path="/blogs/solar-models-explained">
            <BlogSolarModels />
          </Route>
          <Route exact path="/blogs/msme-solar-emi-financing">
            <BlogMSMESolarEMI />
          </Route>
          <Route exact path="/blogs/mc4-connector-guide">
            <BlogMC4 />
          </Route>
          <Route exact path="/blogs/hidden-costs-not-going-solar-2026">
            <BlogHiddenCosts />
          </Route>
          {/* Dynamic blog route - must come after all specific blog routes */}
          <Route exact path="/blogs/:slug">
            <DynamicBlogPost />
          </Route>
          <Route path="/callback">
            <CallbackPage />
          </Route>
          <Route path="/oauth/callback">
            <OAuthCallback />
          </Route>
          <Route path="/solar-calculator">
            <SolarCalculator />
          </Route>
          <Route path="/our-presence">
            <OurPresencePage />
          </Route>
          <Route exact path="/why-choose-us">
            <WhyChooseUs />
          </Route>
          <Route path="/residential-solar">
            <ResidentialSolar />
          </Route>
          <Route path="/commercial-solar">
            <CommercialSolar />
          </Route>
          <Route path="/utility-scale-solar">
            <UtilityScaleSolar />
          </Route>
          {/* Specific service pages - must come before dynamic city route */}
          <Route path="/solar-cold-storage">
            <ColdStorageSolar />
          </Route>
          <Route path="/solar-warehouses">
            <WarehousesSolar />
          </Route>
          {/* City Landing Pages - Dynamic route (must come after specific routes) */}
          <Route path="/solar-:cityName">
            <CityLandingPage />
          </Route>
          {/* Referral Landing Page - Not indexed by search engines */}
          {/* Must be before catch-all route */}
          <Route path="/referral/terms">
            <ReferralTerms />
          </Route>
          <Route path="/referral">
            <ReferralLandingPage />
          </Route>
          <Route path="/privacy-policy">
            <PrivacyPolicy />
          </Route>
          {/* Admin Dashboard - Hidden route, password protected, not indexed */}
          <Route path="/admin-analytics">
            <AdminDashboard />
          </Route>
          {/* Catch-all route for 404 handling */}
          <Route path="*">
            <Redirect to="/" />
          </Route>
        </Switch>
      </Router>
    </ErrorBoundary>
  );
}

export default App;


